import { useTranslation } from "react-i18next";
import type { FurnitureCatalog } from "../../models/apiTypes";
import {
  getFurnitureThumbPath,
  getFurniturePrice,
  getFurnitureDescription,
  getFurnitureName,
  isWallMountedFurniture,
  isFurnitureSoldOut,
  isMusicalInstrumentCatalog,
  getMusicalInstrumentHint,
} from "../../data/furniture";
import { format_num } from "../../utils/format/format_num";
import { cn } from "../../ui/cn";


const SHELL_ICON = '/ui/shell-icon.png';
const STARS_ICON = '/ui/telegram-stars-icon.png';

interface FurnitureShopItemCardProps {
  furniture: FurnitureCatalog;
  isLoading: boolean;
  isAdmin: boolean;
  onTry: (furniture: FurnitureCatalog) => void;
  onBuyFree: (furniture: FurnitureCatalog) => void;
  onBuy: (furniture: FurnitureCatalog) => void;
  /** Interior tutorial: the card to buy (no price shown) or one stepping back. */
  practice?: 'target' | 'dimmed';
}

export default function FurnitureShopItemCard({
  furniture,
  isLoading,
  isAdmin,
  onTry,
  onBuyFree,
  onBuy,
  practice,
}: FurnitureShopItemCardProps) {
  const { t } = useTranslation();
  const { price, currency } = getFurniturePrice(furniture);
  const isFree = price === null || price === 0;
  const isSoldOut = isFurnitureSoldOut(furniture);

  return (
    <article className={cn(
      "box-border flex w-full max-w-[360px] flex-col gap-lg rounded-xl border-2 border-solid border-brand-alpha-50 bg-[linear-gradient(135deg,var(--color-brand-alpha-10)_0%,var(--color-brand-alpha-5)_100%)] px-2xl py-[13px]",
      practice === 'target' && "tutorial-shop-target",
      practice === 'dimmed' && "tutorial-shop-dimmed",
    )}>
      {/* Верхня частина: зображення та кнопки покупки */}
      <div className="flex min-w-0 items-center justify-between gap-md">
        {/* Зображення меблів */}
        <div className="relative h-[143px] w-[143px] shrink-0">
          <div className="absolute inset-0 aspect-square h-[143px] w-[143px] rounded-[18px] bg-[image:var(--gradient-item-spotlight)] blur-[11.75px]" />
          <img
            src={getFurnitureThumbPath(furniture)}
            loading="lazy"
            decoding="async"
            alt={getFurnitureName(furniture.id, t)}
            className="relative m-[15.89px] h-[111.231px] w-[111.231px] rounded-[18px] object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          {isAdmin && !isWallMountedFurniture(furniture) && furniture.category !== 'rugs' && furniture.category !== 'walls' && furniture.category !== 'floors' && (
            <div className="absolute top-xs right-xs flex flex-col items-end gap-[2px]">
              {furniture.footprint_tiles === null && (
                <span className="rounded-[6px] bg-fitness-penalty-alpha-90 px-[5px] py-px text-[11px] leading-[1.4] font-bold text-white">−fp</span>
              )}
              {furniture.surface_tiles === null && (
                <span className="rounded-[6px] bg-surface-penalty-alpha-90 px-[5px] py-px text-[11px] leading-[1.4] font-bold text-white">−sf</span>
              )}
            </div>
          )}
        </div>

        {/* Buy section: price above + gold button */}
        <div className="flex min-w-0 max-w-full flex-1 flex-col items-center gap-md">
          {/* Price display (hidden on the practice purchase: it costs nothing) */}
          {!isFree && practice !== 'target' && (
            <div className="flex items-center justify-center gap-xs">
              <span className="[font-family:'Rubik_One',sans-serif] text-[16px] font-normal text-white">
                {format_num(price!)}
              </span>
              {currency === 'stars' ? (
                <img src={STARS_ICON} alt="Stars" draggable={false} className="h-[20px] w-[20px] shrink-0" />
              ) : (
                <img src={SHELL_ICON} alt={t("Shell")} draggable={false} className="h-[18px] w-[22px] shrink-0" />
              )}
            </div>
          )}
          {isFree && (
            <span className="[font-family:'Rubik_One',sans-serif] text-[14px] text-brand-alpha-90">
              {t("Free")}
            </span>
          )}

          {/* Test drive button — available for all furniture including walls/floors */}
          {practice !== 'target' && <button
            onClick={() => onTry(furniture)}
            className="box-border inline-flex max-w-full cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-brand-alpha-95 px-xl py-sm [font-family:'Rubik_One',sans-serif] text-[14px] leading-[24px] font-normal tracking-[-0.5px] whitespace-nowrap text-white"
          >
            {t("Preview")}
          </button>}

          {/* Gold "Buy Now" button — always enabled (except sold-out items) */}
          {isSoldOut ? (
            // Grey, not clickable; the label stays English on purpose (owner's request).
            <button
              type="button"
              disabled
              className="box-border inline-flex max-w-full cursor-not-allowed items-center justify-center gap-sm rounded-pill border-none bg-sold-out-button px-xl py-sm [font-family:'Rubik_One',sans-serif] text-[14px] leading-[29px] font-normal tracking-[-0.747px] whitespace-nowrap text-white-alpha-70"
            >
              Sold out
            </button>
          ) : isFree ? (
            <button
              onClick={() => onBuyFree(furniture)}
              disabled={isLoading}
              className={cn(
                "box-border inline-flex max-w-full items-center justify-center gap-sm rounded-pill border-none px-xl py-sm [font-family:'Rubik_One',sans-serif] text-[14px] leading-[29px] font-normal tracking-[-0.747px] whitespace-nowrap text-black shadow-[0_4px_12px_var(--color-accent-alpha-40)]",
                isLoading
                  ? "cursor-not-allowed bg-accent-alpha-50"
                  : "cursor-pointer bg-[image:var(--gradient-reward-action)]",
              )}
            >
              {/* Free items (e.g. City Hall walls/floors) are just placed — a short verb */}
              {isLoading ? t("Placing...") : t("Place")}
            </button>
          ) : (
            <button
              data-tutorial={`shop-buy-${furniture.id}`}
              onClick={() => onBuy(furniture)}
              disabled={isLoading}
              className={cn(
                "box-border inline-flex max-w-full items-center justify-center gap-sm rounded-pill border-none px-xl py-sm [font-family:'Rubik_One',sans-serif] text-[14px] leading-[29px] font-normal tracking-[-0.747px] whitespace-nowrap text-black shadow-[0_4px_12px_var(--color-accent-alpha-40)]",
                isLoading
                  ? "cursor-not-allowed bg-accent-alpha-50"
                  : "cursor-pointer bg-[image:var(--gradient-reward-action)]",
              )}
            >
              {t("Buy Now")}
            </button>
          )}
        </div>
      </div>

      {/* Назва та опис */}
      <div className="flex flex-col gap-md">
        <h3 className="m-0 [font-family:'Rubik_One',sans-serif] text-[18px] leading-[30px] font-normal text-white">
          {getFurnitureName(furniture.id, t)}
        </h3>
        {getFurnitureDescription(furniture.id, t) && (
          <p className="mt-sm mb-0 self-stretch [font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-white not-italic">
            {getFurnitureDescription(furniture.id, t)}
          </p>
        )}
        {isMusicalInstrumentCatalog(furniture.id) && (
          <p className="m-0 [font-family:Lato,sans-serif] text-[13px] leading-[18px] font-bold text-brand-alpha-90">
            🎵 {getMusicalInstrumentHint(t)}
          </p>
        )}
        {/* The practice room is not the player's house: their real count would mislead. */}
        {!practice && furniture.purchased_count !== undefined && furniture.purchased_count > 0 && (
          <p className="mt-md mb-0 [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-semibold tracking-[-0.747px] text-brand-alpha-90">
            {t("You have")}: {furniture.purchased_count}
          </p>
        )}
        {furniture.max_quantity !== null && !isSoldOut && (
          <p className="m-0 [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-light tracking-[-0.747px] text-white-alpha-70">
            {t("Available")}: {furniture.max_quantity}
          </p>
        )}
      </div>
    </article>
  );
}
